import type { ListedConnector } from "../../../api/connectorsApi";
import { onSignOut } from "../../../session/signOutCleanups";
import { readStoredString, writeStoredString } from "../../../shared/storage/storedString";

/**
 * Which paired PC this browser is on (F78, F86; W-06 design B, signed off 29 Oct): the browser
 * the connector opened for pairing marks itself (`on_this_pc=1` on the pairing page), and so
 * does the browser whose "Connect this PC" link paired a PC with its ticket (F122), once the
 * server says which; the person can say it themselves. The mark lives in this browser only,
 * keyed to the signed-in account, so a shared browser never shows one person's PC as
 * another's, and it goes when that account signs out here. The server learns nothing new, and
 * the mark never authorises anything: every action still names its PC.
 */

/** A paired PC's ID, or `none` once the person said this browser is on none of them (IDs are UUIDs, never "none"). */
export type ThisPcMark = string | null;

/** What the Connectors page shows first. */
export type ThisPcView =
  | { readonly kind: "no_pc" }
  /** This PC is known: one paired PC (F78), or the marked one; the others come after it. */
  | { readonly kind: "this_pc"; readonly pc: ListedConnector; readonly others: readonly ListedConnector[] }
  /** Several PCs, no mark: ask once, "Which one are you at?". */
  | { readonly kind: "ask"; readonly pcs: readonly ListedConnector[] }
  /** The person said this browser is on none of them. */
  | { readonly kind: "not_here"; readonly pcs: readonly ListedConnector[] };

/** The Connectors page's first answer, from the person's active PCs and this browser's mark. Pure. */
export function thisPcViewOf(pcs: readonly ListedConnector[], mark: ThisPcMark): ThisPcView {
  if (pcs.length === 0) return { kind: "no_pc" };
  if (mark === "none") return { kind: "not_here", pcs };
  // A mark for a PC that is gone (revoked) counts as none.
  const marked = mark === null ? undefined : pcs.find((pc) => pc.connectorId === mark);
  const pc = marked ?? (pcs.length === 1 ? pcs[0] : undefined);
  if (pc === undefined) return { kind: "ask", pcs };
  return { kind: "this_pc", pc, others: pcs.filter((other) => other !== pc) };
}

/**
 * Whether this browser is known to be on another PC than `connectorId`: the person said so, or
 * marked another of their PCs as this one. Never guessed from a single paired PC, unlike the
 * Connectors page's first answer: a Thread only says "not this PC" when it is sure. Pure.
 */
export function isAnotherPc(pcs: readonly ListedConnector[], mark: ThisPcMark, connectorId: string): boolean {
  if (mark === null) return false;
  if (mark === "none") return true;
  return mark !== connectorId && pcs.some((pc) => pc.connectorId === mark);
}

/** The address parameter the connector adds to the pairing page it opens on its own PC. */
export const ON_THIS_PC_PARAMETER = "on_this_pc";

function storageKeyOf(userId: string): string {
  return `dmw.thisPc.${userId}`;
}

export function readThisPcMark(userId: string): ThisPcMark {
  return readStoredString(storageKeyOf(userId));
}

/** Remember which PC this browser is on, `none`, or forget it (`null`). */
export function writeThisPcMark(userId: string, mark: ThisPcMark): void {
  writeStoredString(storageKeyOf(userId), mark);
}

function ticketStorageKeyOf(userId: string): string {
  return `dmw.thisPcTicket.${userId}`;
}

/** The ID, not the secret, of the ticket this browser's last "Connect this PC" link carried, until its pairing is known. */
export function readPairingTicketId(userId: string): string | null {
  return readStoredString(ticketStorageKeyOf(userId));
}

export function writePairingTicketId(userId: string, ticketId: string | null): void {
  writeStoredString(ticketStorageKeyOf(userId), ticketId);
}

onSignOut((userId) => {
  writeThisPcMark(userId, null);
  writePairingTicketId(userId, null);
});
